import React, {useEffect, useState} from 'react';
import {DateTime} from 'luxon';
import DatePicker from 'react-datepicker';
import OddsComparisonTable, {ITableCompetition} from '../components/OddsComparisonTable';
import LoadingSpinner from '../components/LoadingSpinner';
import FailedToLoadData from '../components/FailedToLoadData';
import AlertNoData from '../components/AlertNoData';
import FilterBtn from '../components/FilterBtn';
import {
  fetchDataForFilters,
  fetchTableData,
  fetchWidgetData,
  IWidgetResponse
} from '../api/odds-comparison';
import {
  ICompetition,
  ISport,
  IStatesArr,
  IDropdownItem,
  ITabItem,
  IWidgetData
} from '../utils/types';
import bn from '../utils/bemnames';
import {_t} from '../utils/translations'
import {getCompetitionName} from '../utils/competition'
import {ReactComponent as CalendarSvgIcon} from '../svg/calendar.svg'
import Banner from '../components/Banner'
import FilterDropdown from "../components/FilterDropdown";
import Tabs from "../components/Tabs";
import periods from "../utils/periods";
import marketTypes from "../utils/market-types";
import {useGlobalState} from "../contexts/GlobalState";
import {SportIDs} from "../utils/sport";
import {processResponse} from '../utils/odds-comparison';
import useCheckMobileScreen from '../hooks/useCheckMobileScreen';
import './../styles/main-odds-comparison.scss';
import {setWidgetProviders} from '../utils/widget-providers';

const bem = bn.create('odds-comp');

interface IProps {
  widgetID: number;
  elem: Element,
  bannerCodeDesktop?: string;
  bannerCodeMobile?: string;
  competitionId?: null | number;
}

enum day {
  TODAY = 'today',
  LIVE = 'live',
  TOMORROW = 'tomorrow'
}

const OddsComparison: React.FC<IProps> = (props) => {
  const {
    widgetID,
    elem,
    competitionId = null,
    bannerCodeDesktop = null,
    bannerCodeMobile = null,
  } = props;
  const [widgetData, setWidgetData] = useState<IWidgetData|null>(null)
  const [loadingDataForFilters, setLoadingDataForFilters] = useState<boolean>(true)
  const [failedDataForFilters, setFailedDataForFilters] = useState<boolean>(false)
  const [loadingTableData, setLoadingTableData] = useState<boolean>(false)
  const [failedTableData, setFailedTableData] = useState<boolean>(false)
  const [sports, setSports] = useState<ISport[]>([])
  const [competitions, setCompetitions] = useState<ICompetition[]>([])
  const [statesArr, setStatesArr] = useState<IStatesArr>({
    stateIso2Code: null,
    states: null
  })
  const [selSportId, setSelSportId] = useState<number | null>(null)
  const [selCompetitionId, setSelCompetitionId] = useState<number | null>(null)
  const [tableData, setTableData] = useState<ITableCompetition[]>([])
  const [date, setDate] = useState<string>(DateTime.now().toISODate())
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [endDate, setEndDate] = useState<Date | null>(new Date(new Date().setDate(new Date().getDate() + 6)))
  const [useRangeDate, setUseRangeDate] = useState<boolean>(true)
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0)
  const [selectedMarketType, setSelectedMarketType] = useState<number>(0)
  const [title, setTitle] = useState<string>('')
  const [statesOptions, setStatesOptions] = useState<IDropdownItem[]>([])
  const [competitionOptions, setCompetitionOptions] = useState<IDropdownItem[]>([])
  const {globalStateDispatch} = useGlobalState()
  const isMobile = useCheckMobileScreen(elem)

  const loadDataForFilters = () => {
    setLoadingDataForFilters(true)
    setFailedDataForFilters(false)

    // Fetch data for filters.
    fetchWidgetData({
      widgetId: widgetID,
      competitionId: competitionId,
    }).then((r: IWidgetResponse) => {
      const processedWidgetResponse = processResponse(r)
      setWidgetData(processedWidgetResponse)
      globalStateDispatch({type: 'setOddsComparisonWidget', payload: r})
      setWidgetProviders(r.widgetProviders)

      // Update filer buttons
      fetchDataForFilters({
        widgetID: widgetID,
        sportIds: processedWidgetResponse.widgetSports,
        competitionIds: processedWidgetResponse.competitionIds,
      }).then(r => {
        setLoadingDataForFilters(false)
        setFailedDataForFilters(false)
        setSports(r.sports)
  
        let competitionsList = r.competitions.map(competition => {
          const widgetCompetition = processedWidgetResponse.widgetCompetitions.find(widgetCompetition => widgetCompetition.competitionId === competition.id)
          if (typeof widgetCompetition !== 'undefined') {
            competition.widgetCompetition = widgetCompetition
          }
          return competition
        }).sort((a, b) => a.id < b.id ? -1 : 1)
        setCompetitions(competitionsList)
        setStatesArr(r.statesArr)
      }).catch(() => {
        setLoadingDataForFilters(false)
        setFailedDataForFilters(true)
      });
    })
  }

  const loadTableData = () => {
    if (!widgetData) {
      console.log('Widgets: OC Widget error. widgetData not set.')
      return;
    }

    if (competitions.length > 0 && selectedPeriod !== 0 && selectedMarketType !== 0) {
      let additionalTitleValue = competitions.length === 1 ? competitions[0].trName : sports.length === 1 ? sports[0].trName : ''
      setTitle(additionalTitleValue ? _t('Odds Comparison') + ' - ' + additionalTitleValue : _t('Odds Comparison'))

      let statesList = statesArr.states && statesArr.states.length > 0 ? statesArr.states.map((item): IDropdownItem => {
        return {value: item.id, name: item.name}
      }) : []
      setStatesOptions(statesList)

      let competitionsList = competitions.map((item): IDropdownItem => {
        return {value: item.id, name: item.name};
      })
      setCompetitionOptions(competitionsList)

      setLoadingTableData(true)
      setFailedTableData(false)

      // Get competition IDs.
      const competitionIds = [];
      if (selCompetitionId) {
        competitionIds.push(selCompetitionId)
      } else {
        for (const competition of competitions) {
          if (!selSportId || competition.sportId === selSportId) {
            competitionIds.push(competition.id)
          }
        }
        setSelCompetitionId(competitions[0].id)
      }
      competitionIds.sort((a, b) => a - b)

      let minStartTime: string, maxStartTime: string;

      if (useRangeDate) {

        // Min Date
        const isoMinDateTime = parseCalendarDateTime(startDate.toISOString()).toISODate()
        const techieOffsetMin = parseCalendarDateTime(startDate.toISOString()).toFormat('ZZZ');
        minStartTime = isoMinDateTime + 'T00:00:00' + techieOffsetMin;

        // Max date
        if (endDate === null) {
          maxStartTime = isoMinDateTime + 'T23:59:59' + techieOffsetMin;
        } else {
          const isoMaxDateTime = parseCalendarDateTime(endDate.toISOString()).toISODate()
          const techieOffsetMax = parseCalendarDateTime(endDate.toISOString()).toFormat('ZZZ');
          maxStartTime = isoMaxDateTime + 'T23:59:59' + techieOffsetMax;
        }

      } else {
        // Calculate min start time and max start time by date.
        const dt = parseCalendarDateTime(date);
        const isoDate = dt.toISODate();
        const techieOffset = dt.toFormat('ZZZ');
        minStartTime = isoDate + 'T00:00:00' + techieOffset;
        maxStartTime = isoDate + 'T23:59:59' + techieOffset; // endDate
      }
      // Get provider IDs.
      let providerIds = widgetData.widgetProviders.map(provider => provider.id)
      providerIds.sort((a, b) => a - b)

      setLoadingTableData(true)
      // Fetch table data.
      fetchTableData({
        widgetID,
        competitionIds,
        providerIds,
        minStartTime,
        maxStartTime,
        live: (date === day.LIVE) ? 1 : 0,
        statesArr: statesArr.stateIso2Code,
        periodId: selectedPeriod,
        marketTypeId: selectedMarketType
      }).then(r => {
        setLoadingTableData(false)
        setFailedTableData(false)
        setTableData(r)
      }).catch(() => {
        setLoadingTableData(false)
        setFailedTableData(true)
      })
    }
  }

  const changeDate = (date: string) => {
    setDate(date)
    setStartDate(new Date())
    setEndDate(null)
    setUseRangeDate(false)
  }

  const changeDateCalendar = (dateObj: any) => {
    let [start, end] = dateObj;
    setDate(start.toISOString().substr(0, 10))
    setStartDate(start)
    setEndDate(end)
    setUseRangeDate(true)
  }

  const getDateObjByDate = (date: string): Date => {
    switch (date) {
      case day.TODAY:
      case day.LIVE:
        date = DateTime.now().toISODate()
        break

      case day.TOMORROW:
        date = DateTime.now().plus({days: 1}).toISODate()
        break
    }

    return new Date(date)
  }

  const parseCalendarDateTime = (dateString: string): DateTime => {
    let dt
    switch (dateString) {
      case 'today':
      case 'live':
        dt = DateTime.now()
        break

      case 'tomorrow':
        dt = DateTime.now().plus({days: 1})
        break

      default:
        dt = DateTime.fromISO(dateString)
        break
    }

    return dt
  }

  const handleSportFilterClick = (selSportId: number | null) => {
    if (!loadingTableData) {
      setSelSportId(selSportId)
      setSelCompetitionId(null)
    }
  }

  const handleLeagueFilterClick = (selCompetitionId: number | null) => {
    if (!loadingTableData) {
      setSelCompetitionId(selCompetitionId)
    }
  }

  const handleCountryStateChange = (selectedState: IDropdownItem | null) => {
    let countryIndex = statesArr.states?.findIndex((item) => {
      return item.id === selectedState?.value
    })
    let country = statesArr.states && countryIndex ? statesArr[countryIndex] : null
    setStatesArr({
      stateIso2Code: country ? country.iso2 : null,
      states: statesArr.states,
    })
  }

  const handleCompetitionStageChange = (selectedState: IDropdownItem | null) => {
    let competitionIndex = competitions.findIndex((item) => {
      return item.id === selectedState?.value;
    });
    let competition = competitions[competitionIndex];
    setSelCompetitionId(competition ? competition.id : null)
  }

  const getCalendarButtonName = (date: string, isCustomDate: boolean, useRangeDate: boolean, startDate: Date, endDate: Date | null): string => {
    if (!useRangeDate) {
      return isCustomDate ? DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT) : _t('Select dates')
    } else {

      if (endDate === null) {
        return startDate.toLocaleDateString('en-US')
      } else {
        return `${startDate.toLocaleDateString('en-US')} - ${endDate.toLocaleDateString('en-US')}`
      }
    }
  }

  const onPeriodSwitched = (tab: ITabItem) => {
    setSelectedPeriod(tab.value)
  }

  const onMarketTypeSwitched = (tab: ITabItem) => {
    setSelectedMarketType(tab.value)
  }

  const scrollPeriods = (direction: string) => {
    const periodElement = document.getElementById('tabs')
    if (periodElement) {
      if (direction === 'r')
        periodElement.scrollLeft += 50
      else periodElement.scrollLeft -= 50
    }
  }

  const getFilteredState = () => {
    return statesArr.states && statesArr.states.filter(state => state.iso2 === statesArr.stateIso2Code)
  }

  const getSelectedState = (): IDropdownItem | null => {
    if (statesArr.stateIso2Code) {
      let filteredState = getFilteredState()

      return filteredState && filteredState.length > 0 ? {
        value: filteredState[0].id,
        name: filteredState[0].name
      } : null;
    }

    return null
  }

  const isCustomDate = () => {
    return date.length === 10
  }

  const getSelectedCompetition = (): IDropdownItem | null => {
    let competitionIndex = selCompetitionId ? competitions.findIndex(item => item.id === selCompetitionId) : 0

    return competitions && competitions.length > 0 ? {
      value: competitions[competitionIndex].id,
      name: competitions[competitionIndex].name
    } : null
  }

  const getPeriodOptions = (): ITabItem[] => {
    let competition = competitions.find((item) => item.id === selCompetitionId)
    let moneyLinesMarket = competition?.sportId === SportIDs.Football ? marketTypes.MT_1X2_ID : marketTypes.MT_12_ID
    return [
      {value: moneyLinesMarket === marketTypes.MT_1X2_ID ? periods.P_FT_ID : periods.P_FE_ID, label: _t('FULL GAME')},
      {value: periods.P_1H_ID, label: _t('1st HALF')},
      {value: periods.P_2H_ID, label: _t('2nd HALF')},
      {value: periods.P_1Q_ID, label: _t('1Q')},
      {value: periods.P_2Q_ID, label: _t('2Q')},
      {value: periods.P_3Q_ID, label: _t('3Q')},
      {value: periods.P_4Q_ID, label: _t('4Q')}
    ]
  }

  const getMarketTypeOptions = (): ITabItem[] => {
    let competition = competitions.find((item) => item.id === selCompetitionId)
    return [
      {value: competition?.sportId === SportIDs.Football ? marketTypes.MT_1X2_ID : marketTypes.MT_12_ID, label: _t('Money Lines')},
      {value: marketTypes.MT_AH_ID, label: _t('Point Spreads')},
      {value: marketTypes.MT_OU_ID, label: _t('Totals')}
    ]
  }

  useEffect(loadTableData, [competitions, date, endDate, selCompetitionId, selSportId, sports, startDate, statesArr, useRangeDate, widgetID, selectedPeriod, selectedMarketType, widgetData])

  useEffect(loadDataForFilters, [widgetID, competitionId, globalStateDispatch])

  useEffect(() => {
    let marketTypeId = selectedMarketType
    let periodId = selectedPeriod
    let competition = undefined

    if (selCompetitionId)
      competition = competitions.find((item) => item.id === selCompetitionId)
    else competition = competitions[0]

    if ((marketTypeId === 0 || periodId === 0) && competition) {
      if (marketTypeId === 0) {
        marketTypeId = competition?.sportId === SportIDs.Football ? marketTypes.MT_1X2_ID : marketTypes.MT_12_ID
        setSelectedMarketType(marketTypeId)
      }
      if (periodId === 0) {
        periodId = marketTypeId === marketTypes.MT_1X2_ID ? periods.P_FT_ID : periods.P_FE_ID
        setSelectedPeriod(periodId)
      }
    }
  }, [competitions, selCompetitionId, selectedPeriod, selectedMarketType]);


  if (widgetData?.format === 'eu') {
    return (
      <div className={(isMobile) ? bem.e('mobile') : ''}>
        {loadingDataForFilters && <LoadingSpinner/>}
        {!loadingDataForFilters && failedDataForFilters && (
          <FailedToLoadData onReload={loadDataForFilters}/>
        )}
        {!loadingDataForFilters && !failedDataForFilters && !competitions.length && (
          <AlertNoData/>
        )}
        {!loadingDataForFilters && !failedDataForFilters && competitions.length > 0 && (
          <>
            <div className={bem.e('title-and-state-container')}>
              <h2 className={bem.e('title')}>{title}</h2>
              {statesArr.states && statesArr.states.length > 1 && (
                <div className={bem.e('states-dropdown')}>
                  <FilterDropdown dropdownList={statesOptions} selectedItem={getSelectedState()}
                                  onStateChange={handleCountryStateChange}/>
                </div>
              )}
            </div>
            <div className={bem.e('filter-form')}>
              {sports.length > 1 && (
                <div className={bem.e('filter-group')}>
                  <div className={bem.e('filter-label')}>{_t('Sports')}</div>
                  <div className={bem.e('filter-btns-wrp')}>
                    <FilterBtn
                      checkMark
                      filterKey={null}
                      activeKey={selSportId}
                      onClick={handleSportFilterClick}
                    >
                      <span>{_t('All')}</span>
                    </FilterBtn>
                    {sports.map(sport => {
                      return (
                        <FilterBtn
                          key={sport.id}
                          checkMark
                          filterKey={sport.id}
                          activeKey={selSportId}
                          onClick={handleSportFilterClick}
                        >
                          <span>{sport.trName}</span>
                        </FilterBtn>
                      );
                    })}
                  </div>
                </div>
              )}
              {competitions.length > 1 && (
                <div className={bem.e('filter-group')}>
                  <div className={bem.e('filter-label')}>{_t('League')}</div>
                  <div className={bem.e('filter-btns-wrp')}>
                    <FilterBtn
                      checkMark
                      filterKey={null}
                      activeKey={selCompetitionId}
                      onClick={handleLeagueFilterClick}
                    >
                      <span>{_t('All')}</span>
                    </FilterBtn>
                    {competitions.map(competition => {
                      if (selSportId && competition.sportId !== selSportId) {
                        return null;
                      }

                      return (
                        <FilterBtn
                          key={competition.id}
                          checkMark
                          filterKey={competition.id}
                          activeKey={selCompetitionId}
                          onClick={handleLeagueFilterClick}
                        >
                          <span>{getCompetitionName(competition)}</span>
                        </FilterBtn>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className={bem.e('filter-group')}>
                <div className={bem.e('filter-row')}>
                  <div className={bem.e('filter-col')}>
                    <div className={bem.e('filter-label')}>{_t('Select date')}</div>
                    <div className={bem.e('filter-btns-wrp')}>
                      <div className={bem.e('filter-btns-wrp')}>
                        <DatePicker
                          selected={getDateObjByDate(date)}
                          onChange={changeDateCalendar}
                          startDate={startDate}
                          endDate={endDate}
                          selectsRange
                          shouldCloseOnSelect={false}
                          customInput={
                            <FilterBtn
                              filterKey={isCustomDate() ? date : ''}
                              activeKey={date}
                              clickOnActive
                            >
                            <span className={bem.e('svg-mark')}>
                              <CalendarSvgIcon/>
                            </span>
                              <span>
                              {getCalendarButtonName(date, isCustomDate(), useRangeDate, startDate, endDate)}
                            </span>
                            </FilterBtn>
                          }
                        />
                      </div>
                      <FilterBtn
                        checkMark
                        filterKey={day.TODAY}
                        activeKey={date}
                        onClick={changeDate}
                      >
                        <span>{_t('Today')}</span>
                      </FilterBtn>
                      <FilterBtn
                        checkMark
                        filterKey={day.TOMORROW}
                        activeKey={date}
                        onClick={changeDate}
                      >
                        <span>{_t('Tomorrow')}</span>
                      </FilterBtn>
                    </div>
                  </div>
                  <div className={bem.e('filter-col')}>
                    <div className={bem.e('filter-label')}>{_t('Current')}</div>
                    <div className={bem.e('filter-btns-wrp')}>
                      <FilterBtn
                        filterKey={day.LIVE}
                        activeKey={date}
                        onClick={changeDate}
                      >
                        <span className={bem.e('filter-live-arrow')}/>
                        <span>Live</span>
                      </FilterBtn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {loadingTableData && <LoadingSpinner/>}
            {!loadingTableData && failedTableData && <FailedToLoadData onReload={loadTableData}/>}
            {!loadingTableData && !failedTableData && tableData.length > 0 && (
              <>
                <OddsComparisonTable
                  competitions={tableData}
                  oddsFormat={widgetData.oddsFormat}
                  outLinkFolder={widgetData.outLinkFolder}
                  isMobile={isMobile}
                  clickableRows={widgetData.clickableRows}
                  showTeamLogos={widgetData.showTeamLogos}
                  linkTemplate={widgetData.linkTemplate}
                  sports={sports}
                  format={widgetData.format}
                  period={selectedPeriod}
                  marketType={selectedMarketType}
                  logoType={widgetData.providerLogoType}
                  selCompetitionId={selCompetitionId}
                />
                <Banner
                  isMobile={isMobile}
                  bannerCodeMobile={bannerCodeMobile}
                  bannerCodeDesktop={bannerCodeDesktop}
                />
              </>
            )}
            {!loadingTableData && !failedTableData && !tableData.length && (
              <AlertNoData text={_t('There are no odds available')}/>
            )}
          </>
        )}
      </div>
    )
  } else {
    return (
      <div className={(isMobile) ? bem.e('mobile') : ''}>
        {loadingDataForFilters && <LoadingSpinner/>}
        {!loadingDataForFilters && failedDataForFilters && (
          <FailedToLoadData onReload={loadDataForFilters}/>
        )}
        {!loadingDataForFilters && !failedDataForFilters && !competitions.length && (
          <AlertNoData/>
        )}
        {!loadingDataForFilters && !failedDataForFilters && competitions.length > 0 && (
          <>
            <div className={bem.e('filter-form')}>
              {competitions.length > 0 && (
                <div className={bem.e('us-filters')}>
                  <div className={bem.e('filter-group us')}>
                    <div className={bem.e('filter-label')}>
                      {getSelectedCompetition()?.name ?? _t('All')}
                    </div>
                    <div className={bem.e('competitions-dropdown')}>
                      <FilterDropdown dropdownList={competitionOptions} selectedItem={getSelectedCompetition()}
                                      onStateChange={handleCompetitionStageChange} withDefault={true}/>
                    </div>
                    <div className={bem.e('date-filter us')}>
                      <DatePicker
                        selected={getDateObjByDate(date)}
                        onChange={changeDateCalendar}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        shouldCloseOnSelect={false}
                        customInput={
                          <FilterBtn
                            filterKey={isCustomDate() ? date : ''}
                            activeKey={date}
                            clickOnActive
                          >
                              <span className={bem.e('svg-mark')}>
                                <CalendarSvgIcon/>
                              </span>
                            <span>
                                {getCalendarButtonName(date, isCustomDate(), useRangeDate, startDate, endDate)}
                              </span>
                          </FilterBtn>
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className={bem.e('periods-wrapper')}>
                {isMobile && (
                  <div className={bem.e('scroll-btn left')} onClick={() => scrollPeriods('l')}></div>
                )}
                <Tabs items={getPeriodOptions()} onStateChange={onPeriodSwitched} pointer={true}/>
                {isMobile && (
                  <div className={bem.e('scroll-btn right')} onClick={() => scrollPeriods('r')}></div>
                )}
              </div>
              <Tabs items={getMarketTypeOptions()} onStateChange={onMarketTypeSwitched} pointer={false}/>
            </div>
            {loadingTableData && <LoadingSpinner/>}
            {!loadingTableData && failedTableData && <FailedToLoadData onReload={loadTableData}/>}
            {!loadingTableData && !failedTableData && tableData.length > 0 && widgetData && (
              <>
                <OddsComparisonTable
                  competitions={tableData}
                  oddsFormat={widgetData.oddsFormat}
                  outLinkFolder={widgetData.outLinkFolder}
                  isMobile={isMobile}
                  clickableRows={widgetData.clickableRows}
                  showTeamLogos={widgetData.showTeamLogos}
                  linkTemplate={widgetData.linkTemplate}
                  sports={sports}
                  format={widgetData.format}
                  period={selectedPeriod}
                  marketType={selectedMarketType}
                  channel={widgetData.channel}
                  logoType={widgetData.providerLogoType}
                  selCompetitionId={selCompetitionId}
                />
                <Banner
                  isMobile={isMobile}
                  bannerCodeMobile={bannerCodeMobile}
                  bannerCodeDesktop={bannerCodeDesktop}
                />
              </>
            )}
            {!loadingTableData && !failedTableData && !tableData.length && (
              <AlertNoData text={_t('There are no odds available')}/>
            )}
          </>
        )}
      </div>
    );
  }
}

export default OddsComparison
